import { Body, Column, expandBorderRadii, FormControl, marginUtil, Row } from "@gadgetinc/widgets";
import { AddressElement, CardCvcElement, CardExpiryElement, CardNumberElement, useElements } from "@stripe/react-stripe-js";
import { useStyletron } from "baseui";
import { Skeleton } from "baseui/skeleton";
import React, { useEffect, useMemo, useState } from "react";
import { reportFrontendErrorForInvestigation } from "../../../lib/reportFrontendErrorForInvestigation";
import { Caption } from "../../Caption";
const GoogleMapsApiKey = "AIzaSyB7sOywMeNGRXy9UHP2reDDFzgB63lj3TQ";

const AddPaymentMethodFormLoader = () => {
  const [_, $theme] = useStyletron();

  const skeletonCCRow = (
    <Skeleton
      rows={1}
      animation
      overrides={{
        Row: {
          style: {
            height: "33px",
            borderRadius: "4px",
          },
        },
      }}
    />
  );

  return (
    <Column $gap={$theme.sizing.scale800}>
      <Column $gap={$theme.sizing.scale300}>
        {skeletonCCRow}
        {skeletonCCRow}
      </Column>
      <Column $gap={$theme.sizing.scale300}>
        <Skeleton
          rows={1}
          animation
          overrides={{
            Row: {
              style: {
                height: "33px",
                borderRadius: "4px",
                marginTop: "22px",
              },
            },
          }}
        />
        <Skeleton
          rows={1}
          animation
          overrides={{
            Row: {
              style: {
                height: "33px",
                borderRadius: "4px",
                marginTop: "24px",
              },
            },
          }}
        />
        <Skeleton
          rows={1}
          animation
          overrides={{
            Row: {
              style: {
                height: "33px",
                borderRadius: "4px",
                marginTop: "26px",
              },
            },
          }}
        />
      </Column>
    </Column>
  );
};

export const useAddPaymentForm = () => {
  const [formComplete, setFormComplete] = useState(false);
  const [showFormIncomplete, setShowFormIncomplete] = useState(false);

  return {
    form: {
      setFormComplete,
      showFormIncomplete,
    },
    formComplete,
    setShowFormIncomplete,
  };
};

export const AddPaymentMethodForm = (props: {
  loading: boolean;
  form: {
    setFormComplete: (value: boolean) => void;
    showFormIncomplete: boolean;
  };
  errors?: string[];
  button?: React.ReactNode;
  defaultCountry?: string;
  minHeight?: string;
}) => {
  const { loading, form, errors, button, defaultCountry, minHeight } = props;
  const [_, $theme] = useStyletron();

  const elements = useElements();

  const [elementsLoaded, setElementsLoaded] = useState<{
    cardElementLoaded: boolean;
    expiryElementLoaded: boolean;
    cvcElementLoaded: boolean;
    addressElementLoaded: boolean;
  }>({ cardElementLoaded: false, expiryElementLoaded: false, cvcElementLoaded: false, addressElementLoaded: false });

  const [elementsComplete, setElementsComplete] = useState<{
    cardElementComplete: boolean;
    expiryElementComplete: boolean;
    cvcElementComplete: boolean;
    addressElementComplete: boolean;
  }>({ cardElementComplete: false, expiryElementComplete: false, cvcElementComplete: false, addressElementComplete: false });

  const [cardNumberFocused, setCardNumberFocused] = useState(false);
  const [cardNumberError, setCardNumberError] = useState<string | undefined>();

  const [cardExpiryFocused, setCardExpiryFocused] = useState(false);
  const [cardExpiryError, setCardExpiryError] = useState<string | undefined>();

  const [cardCvcFocused, setCardCvcFocused] = useState(false);
  const [cardCvcError, setCardCvcError] = useState<string | undefined>();

  const formLoading = useMemo(() => {
    return !Object.values(elementsLoaded).every((x) => x);
  }, [elementsLoaded]);

  useEffect(() => {
    form.setFormComplete(Object.values(elementsComplete).every((x) => x));
  }, [form, elementsComplete]);

  const cardNumberFormError = useMemo(() => {
    if (cardNumberError) {
      return cardNumberError;
    } else if (form.showFormIncomplete && !elementsComplete.cardElementComplete) {
      return "Please provide a complete card number.";
    }
  }, [form.showFormIncomplete, cardNumberError, elementsComplete]);

  const cardExpiryCvcFormError = useMemo(() => {
    if (cardExpiryError || cardCvcError) {
      return [cardExpiryError, cardCvcError].filter((err) => !!err).join(" ");
    } else if (form.showFormIncomplete && !(elementsComplete.cardElementComplete && elementsComplete.cvcElementComplete)) {
      return "Please provide a complete card expiry and CVC code.";
    }
  }, [form.showFormIncomplete, cardExpiryError, cardCvcError, elementsComplete]);

  useEffect(() => {
    if (form.showFormIncomplete && elements && !elementsComplete.addressElementComplete) {
      const addressElement = elements?.getElement(AddressElement);
      ((addressElement as any).getValue() as Promise<any>).catch(reportFrontendErrorForInvestigation);
    }
  }, [elements, form.showFormIncomplete, elementsComplete]);

  const showLoadingState = loading || formLoading;
  return (
    <Column
      $style={{
        flex: 1,
        position: "relative",
        minHeight,
      }}
      data-testid="add-payment-method-form"
    >
      {showLoadingState && (
        <Column
          $style={{
            position: "absolute",
            left: 0,
            top: 0,
            flex: 1,
            width: "100%",
            height: "100%",
            zIndex: 999,
          }}
        >
          <AddPaymentMethodFormLoader />
        </Column>
      )}
      <Column
        $gap={$theme.sizing.scale800}
        $style={{
          flex: 1,
          opacity: showLoadingState ? "0" : "1",
          transition: "all .2s",
          visibility: showLoadingState ? "hidden" : "visible",
        }}
      >
        <Column $gap={$theme.sizing.scale300}>
          <FormControl
            error={cardNumberFormError}
            overrides={{
              ControlContainer: {
                style: { ...marginUtil(0) },
              },
            }}
          >
            <Column
              $style={{
                backgroundColor: $theme.colors.primary50,
                padding: $theme.sizing.scale300,
                ...expandBorderRadii($theme.borders.radius200),
                ...(cardNumberFocused
                  ? { outlineStyle: "solid", outlineWidth: $theme.sizing.scale0, outlineColor: $theme.colors.accent }
                  : {}),
              }}
            >
              <CardNumberElement
                onReady={(element) => {
                  setElementsLoaded((curr) => ({ ...curr, cardElementLoaded: true }));
                  element.on("change", (event) => {
                    setCardNumberError(event.error?.message);
                    setElementsComplete((curr) => ({ ...curr, cardElementComplete: event.complete }));
                  });
                  element.on("focus", () => {
                    setCardNumberFocused(true);
                  });
                  element.on("blur", () => {
                    setCardNumberFocused(false);
                  });
                }}
                options={{
                  showIcon: true,
                  style: {
                    base: {
                      fontSize: "14px",
                      fontFamily: $theme.typography.font100.fontFamily,
                    },
                  },
                }}
              />
            </Column>
          </FormControl>
          <FormControl
            error={cardExpiryCvcFormError}
            overrides={{
              ControlContainer: {
                style: { ...marginUtil(0) },
              },
            }}
          >
            <Row
              $style={{
                backgroundColor: $theme.colors.primary50,
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: $theme.colors.primary100,
                ...expandBorderRadii($theme.borders.radius200),
              }}
            >
              <Column
                $style={{
                  flex: 1,
                  borderRightWidth: "1px",
                  borderRightStyle: "solid",
                  borderRightColor: $theme.colors.primary100,
                  padding: $theme.sizing.scale300,
                  ...expandBorderRadii($theme.borders.radius200),
                  ...(cardExpiryFocused
                    ? { outlineStyle: "solid", outlineWidth: $theme.sizing.scale0, outlineColor: $theme.colors.accent }
                    : {}),
                }}
              >
                <CardExpiryElement
                  onReady={(element) => {
                    setElementsLoaded((curr) => ({ ...curr, expiryElementLoaded: true }));
                    element.on("change", (event) => {
                      setCardExpiryError(event.error?.message);
                      setElementsComplete((curr) => ({ ...curr, expiryElementComplete: event.complete }));
                    });
                    element.on("focus", () => {
                      setCardExpiryFocused(true);
                    });
                    element.on("blur", () => {
                      setCardExpiryFocused(false);
                    });
                  }}
                  options={{ style: { base: { fontFamily: $theme.typography.font100.fontFamily } } }}
                />
              </Column>
              <Column
                $style={{
                  flex: 1,
                  padding: $theme.sizing.scale300,
                  ...expandBorderRadii($theme.borders.radius200),
                  ...(cardCvcFocused
                    ? { outlineStyle: "solid", outlineWidth: $theme.sizing.scale0, outlineColor: $theme.colors.accent }
                    : {}),
                }}
              >
                <CardCvcElement
                  onReady={(element) => {
                    setElementsLoaded((curr) => ({ ...curr, cvcElementLoaded: true }));
                    element.on("change", (event) => {
                      setCardCvcError(event.error?.message);
                      setElementsComplete((curr) => ({ ...curr, cvcElementComplete: event.complete }));
                    });
                    element.on("focus", () => {
                      setCardCvcFocused(true);
                    });
                    element.on("blur", () => {
                      setCardCvcFocused(false);
                    });
                  }}
                  options={{ style: { base: { fontFamily: $theme.typography.font100.fontFamily } } }}
                />
              </Column>
            </Row>
          </FormControl>
        </Column>
        <AddressElement
          onReady={() => {
            setElementsLoaded((curr) => ({ ...curr, addressElementLoaded: true }));
          }}
          onChange={(event) => {
            setElementsComplete((curr) => ({ ...curr, addressElementComplete: event.complete }));
          }}
          options={{
            mode: "billing",
            fields: { phone: "never" },
            display: { name: "full" },
            autocomplete: { mode: "google_maps_api", apiKey: GoogleMapsApiKey },
            defaultValues: defaultCountry
              ? {
                  address: {
                    country: defaultCountry,
                  },
                }
              : undefined,
          }}
        />
        {button && (
          <Column $gap={$theme.sizing.scale300}>
            {button}
            {errors &&
              errors.map((error) => {
                return (
                  <Body key={error} style={{ color: $theme.colors.negative400 }}>
                    {error}
                  </Body>
                );
              })}
            <Caption>
              By saving your card information, you allow Gadget to charge your card for future payments in accordance with our terms.
            </Caption>
          </Column>
        )}
      </Column>
    </Column>
  );
};
